/**
 * OutputModeSection
 *
 * Three radio buttons: Sidecar (active), Soft Embedded (disabled), Hard Burn (disabled).
 * When mediaType === 'audio', only Sidecar is shown.
 * Disabled modes show a "Coming soon" tooltip.
 */

import { memo } from 'react';
import { FileText, Film, Flame } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useSubtitles } from '../SubtitlesContext.context';
import type { SubtitleOutputMode } from '../types';

interface OutputModeOptionProps {
  id: SubtitleOutputMode;
  label: string;
  description: string;
  icon: React.ReactNode;
  selected: boolean;
  disabled: boolean;
  comingSoon: boolean;
  onChange: (mode: SubtitleOutputMode) => void;
}

const OutputModeOption = memo(function OutputModeOption({
  id,
  label,
  description,
  icon,
  selected,
  disabled,
  comingSoon,
  onChange,
}: OutputModeOptionProps) {
  return (
    <div className="relative" title={comingSoon ? 'Coming soon' : undefined}>
      <label
        className={[
          'flex items-start gap-3 p-3 sm:p-4 rounded-lg border cursor-pointer transition-colors',
          selected ? 'border-purple-500/50 bg-purple-900/20' : 'border-white/10 bg-black/20',
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:border-purple-500/30 hover:bg-purple-900/10',
        ].join(' ')}
      >
        <input
          type="radio"
          name="output_mode"
          value={id}
          checked={selected}
          disabled={disabled}
          onChange={() => onChange(id)}
          className="mt-0.5 h-4 w-4 accent-purple-500 flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-purple-400">{icon}</span>
            <span className="text-sm font-medium text-white">{label}</span>
            {comingSoon && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-white/10 text-white/50 font-medium">
                Coming soon
              </span>
            )}
          </div>
          <p className="text-xs text-white/50 mt-0.5">{description}</p>
        </div>
      </label>
    </div>
  );
});

export const OutputModeSection = memo(function OutputModeSection() {
  const { t } = useTranslation();
  const { state, isUploading, isGenerating, handleOutputModeChange } = useSubtitles();
  const { outputMode, mediaType } = state;

  const isAudio = mediaType === 'audio';
  const interactionDisabled = isUploading || isGenerating;

  return (
    <div className="panel-glass">
      <h3 className="section-heading">
        <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
        <span>{t('subtitles.outputMode.title', { defaultValue: 'Output Mode' })}</span>
      </h3>

      <div className="space-y-2 sm:space-y-3">
        {/* Sidecar — always available */}
        <OutputModeOption
          id="sidecar"
          label={t('subtitles.outputMode.sidecar', { defaultValue: 'Sidecar Files' })}
          description={t('subtitles.outputMode.sidecarDescription', {
            defaultValue:
              'Generates standalone SRT, VTT and ASS subtitle files in a ZIP archive. Works with any player.',
          })}
          icon={<FileText className="h-4 w-4" />}
          selected={outputMode === 'sidecar'}
          disabled={interactionDisabled}
          comingSoon={false}
          onChange={handleOutputModeChange}
        />

        {/* Soft Embedded — video only, Phase 3 (active) */}
        {!isAudio && (
          <OutputModeOption
            id="soft_embedded"
            label={t('subtitles.outputMode.softEmbedded', { defaultValue: 'Soft Embedded' })}
            description={t('subtitles.outputMode.softEmbeddedDescription', {
              defaultValue:
                'Embeds subtitle tracks directly into the MP4 container. Toggle on/off in any player. Video files only.',
            })}
            icon={<Film className="h-4 w-4" />}
            selected={outputMode === 'soft_embedded'}
            disabled={interactionDisabled}
            comingSoon={false}
            onChange={handleOutputModeChange}
          />
        )}

        {/* Hard Burn — video only, Phase 4 (active) */}
        {!isAudio && (
          <OutputModeOption
            id="hard_burn"
            label={t('subtitles.outputMode.hardBurn', { defaultValue: 'Hard Burn' })}
            description={t('subtitles.outputMode.hardBurnDescription', {
              defaultValue:
                'Burns subtitles directly into the video pixels. Supports one language and custom styling.',
            })}
            icon={<Flame className="h-4 w-4" />}
            selected={outputMode === 'hard_burn'}
            disabled={interactionDisabled}
            comingSoon={false}
            onChange={handleOutputModeChange}
          />
        )}
      </div>
    </div>
  );
});
